import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { ResponseDto, UserDto, RegisterUserDto } from "@mimber/schema"

/**
 * Organization-specific hook for registering new organization users
 */
export const useRegister = (
  options?: UseMutationOptions<
    ResponseDto<UserDto>,
    Error,
    RegisterUserDto
  >
): UseMutationResult<
  ResponseDto<UserDto>,
  Error,
  RegisterUserDto
> => {
  guardAppUsage("mimber-organization", "useRegister")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (registerData: RegisterUserDto) => {
      // identityId is extracted from JWT token on backend, not sent in request body
      const response = await server.post<ResponseDto<UserDto>>(
        "account/register",
        registerData
      )
      return response.data
    },
    onSuccess: (user, variables, onMutateResult, context) => {
      options?.onSuccess?.(user, variables, onMutateResult, context)
      // Invalidate related queries after successful registration
      queryClient.invalidateQueries({ queryKey: ["users"] })
      queryClient.invalidateQueries({ queryKey: ["profile"] })
    },
  })
}
